import { Middleware, UnknownAction } from 'redux'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import {
  setColumnOrder,
  setColumnSizing,
  setColumnVisibility,
} from '@disco/state-management/reducers/search-page'
import { getUserSettings } from '@disco/state-management/selectors/user'
import { PRELOAD_DATA_ON_LOGIN } from '@disco/constants/ActionTypes'

export const SEARCH_TABLE_VISIBILITY_KEY = 'search_column_visibility'
export const SEARCH_TABLE_ORDER_KEY = 'search_column_order'
export const SEARCH_TABLE_SIZING_KEY = 'search_column_sizing'

const settingsByAction = [
  { match: setColumnVisibility.match, key: SEARCH_TABLE_VISIBILITY_KEY },
  { match: setColumnOrder.match, key: SEARCH_TABLE_ORDER_KEY },
  { match: setColumnSizing.match, key: SEARCH_TABLE_SIZING_KEY },
]

/**
 * A middleware that handles search preferences by:
 * 1. Loading saved user preferences after data preload (overrides defaults set by reducer)
 * 2. Saving column visibility/order/sizing changes to user settings
 */
const searchPreferencesSync: Middleware<object, any> = store => {
  // On first load, we need to load the settings from the server and set them in the redux store
  // but we don't want to trigger an update at the same time, as it is not necessary
  let isLoadingFromSettings = false

  return next => (action: UnknownAction) => {
    // Process the action first (standard middleware pattern)
    const result = next(action)

    // Load saved user preferences when data is preloaded
    if (action.type === PRELOAD_DATA_ON_LOGIN) {
      const state = store.getState()
      const userSettings = getUserSettings(state)

      isLoadingFromSettings = true
      try {
        if (userSettings?.[SEARCH_TABLE_VISIBILITY_KEY]) {
          store.dispatch(
            setColumnVisibility(userSettings[SEARCH_TABLE_VISIBILITY_KEY]),
          )
        }
        if (userSettings?.[SEARCH_TABLE_ORDER_KEY]) {
          store.dispatch(setColumnOrder(userSettings[SEARCH_TABLE_ORDER_KEY]))
        }
        if (userSettings?.[SEARCH_TABLE_SIZING_KEY]) {
          store.dispatch(setColumnSizing(userSettings[SEARCH_TABLE_SIZING_KEY]))
        }
      } finally {
        isLoadingFromSettings = false
      }
    }

    // Save changes to user preferences when column visibility/order/sizing changes
    if (!isLoadingFromSettings) {
      const matched = settingsByAction.find(({ match }) => match(action))
      if (matched) {
        store.dispatch(
          updateUserSettings({
            settings: {
              [matched.key]: action.payload,
            },
          }),
        )
      }
    }

    return result
  }
}

export default searchPreferencesSync
